// PayHero Channel Service – API integration for PayHero payment channel management

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE_URL = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
// PayHero routes are mounted at /api/payhero
const PAYHERO_BASE = `${BASE_URL}/payhero`;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PayHeroChannel {
  id: string;
  landlordId: string;
  description: string;       // human-readable label, e.g. "KCB Bank"
  channelType: string;       // "paybill" | "till" | "bank"
  accountId: number;         // PayHero account_id (resolved from env on backend)
  shortCode: string | null;  // paybill / till / bank paybill short code
  accountNumber: string | null; // bank account number
  referenceMode?: 'STATIC' | 'UNIT_VARIABLE';
  referenceTemplate?: string | null;
  propertyOwnerId?: string | null;
  isDefault: boolean;
  isActive: boolean;
  assignedPropertyCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PayHeroChannelData {
  description: string;
  channel_type: string;            // "paybill" | "till" | "bank"
  account_id?: string;             // always sent as "" – resolved from env on backend
  short_code?: string;
  account_number?: string;
  reference_mode?: 'STATIC' | 'UNIT_VARIABLE';
  reference_template?: string;
  propertyOwnerId?: string | null;
  isDefault?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

class PayHeroChannelService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const { authTokenService } = await import('./auth.service');
    const authHeader = authTokenService.getAuthHeader();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as any,
    };
    if (authHeader) headers['Authorization'] = authHeader;

    const response = await fetch(`${PAYHERO_BASE}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ── Channels (org-scoped; landlordId arg kept for call-site compat, unused) ─

  async getChannels(_landlordId?: string): Promise<ApiResponse<PayHeroChannel[]>> {
    return this.makeRequest(`/channels`);
  }

  async addChannel(
    _landlordId: string | undefined,
    data: PayHeroChannelData
  ): Promise<ApiResponse<PayHeroChannel>> {
    return this.makeRequest(`/channels`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChannel(
    _landlordId: string | undefined,
    channelRecordId: string,
    data: Partial<PayHeroChannelData>
  ): Promise<ApiResponse<PayHeroChannel>> {
    return this.makeRequest(`/channels/${channelRecordId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteChannel(
    _landlordId: string | undefined,
    channelRecordId: string
  ): Promise<ApiResponse<void>> {
    return this.makeRequest(`/channels/${channelRecordId}`, { method: 'DELETE' });
  }

  async setDefaultChannel(
    _landlordId: string | undefined,
    channelRecordId: string
  ): Promise<ApiResponse<void>> {
    return this.makeRequest(`/channels/${channelRecordId}/set-default`, {
      method: 'PATCH',
    });
  }

  async assignProperties(
    _landlordId: string | undefined,
    channelRecordId: string,
    propertyIds: string[]
  ): Promise<ApiResponse<{ assigned?: number }> & { assigned?: number }> {
    return this.makeRequest(`/channels/${channelRecordId}/assign-properties`, {
      method: 'POST',
      body: JSON.stringify({ propertyIds }),
    });
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  validateChannelData(data: Partial<PayHeroChannelData>): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!data.description?.trim()) errors.description = 'Channel name is required';
    if (!data.channel_type) errors.channel_type = 'Channel type is required';
    if (!data.short_code?.toString().trim()) {
      errors.short_code =
        data.channel_type === 'till'
          ? 'Till short code is required'
          : 'Paybill number is required';
    }
    if ((data.channel_type === 'paybill' || data.channel_type === 'bank') && !data.account_number?.trim()) {
      errors.account_number = 'Account reference is required';
    }
    if (data.reference_mode === 'UNIT_VARIABLE') {
      if (!data.reference_template?.trim()) {
        errors.reference_template = 'Reference template is required when using unit variable';
      } else if (!data.reference_template.includes('{unit}')) {
        errors.reference_template = 'Reference template must include {unit}';
      }
    }
    return errors;
  }
}

export const payheroChannelService = new PayHeroChannelService();
export default payheroChannelService;
